import { useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { useConfigsWithoutSuspense } from '@/components/ConfigsProvider';
import { NavConfigFragment } from '@/components/ConfigsProvider/graphql/fragment/generated/NavConfig';
import {
  adaptCustomLinkMobileTabs,
  adaptCustomLinkWebTabs,
} from '@/components/Header/components/NavigationTabs/utils';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';

const NO_CONFIGS: never[] = [];

/**
 * Custom links from the nav config, for the unified shell (top bar, sidebar, bottom nav).
 * `previewNavConfig` is the homepage editor's unsaved config; otherwise the saved one is fetched.
 */
function useCustomNavLinks(previewNavConfig?: NavConfigFragment) {
  const { t } = useTranslation();
  const featureFlags = useFeatureFlags();
  const configKey = featureFlags[FeatureFlags.EnableUnifiedDashboard]
    ? 'unifiedNavConfig'
    : 'navConfig';

  const configs = useConfigsWithoutSuspense(previewNavConfig ? NO_CONFIGS : [configKey]);
  const navConfig = previewNavConfig ?? (configs[configKey] as NavConfigFragment | undefined);

  const webTabs = useMemo(
    () => adaptCustomLinkWebTabs(navConfig, featureFlags),
    [navConfig, featureFlags],
  );

  const mobileTabs = useMemo(
    () => adaptCustomLinkMobileTabs({ t, navConfig, featureFlags }),
    [t, navConfig, featureFlags],
  );

  return { webTabs, mobileTabs };
}

export default useCustomNavLinks;
